import React, { memo } from "react";
import PropTypes from "prop-types";

const normalizeStatus = (status = "") => String(status).trim().toLowerCase();

const SUCCESS_STATUSES = new Set([
  "approved",
  "processed",
  "resume",
  "reviewed",
  "minted",
  "completed",
  "published",
  "active",
  "cleared",
  "success",
  "sent",
  "paid",
]);

const DANGER_STATUSES = new Set([
  "rejected",
  "declined",
  "inactive",
  "suspended",
  "deactivated",
  "paused",
  "unreserved",
  "unreserve",
  "burned",
  "escalated",
  "failed",
]);

const WARNING_STATUSES = new Set([
  "on-hold",
  "on_hold",
  "planning",
  "draft",
  "locked",
  "pending",
  "requested",
  "on hold",
  "under review",
]);

const CUSTOM_STATUS_STYLES = {
  suspicious: {
    bg: "bg-[#FFF6E8]",
    text: "text-[#6b7280]",
    variant: "primary",
  },

  unusual: {
    bg: "bg-[#FFF6E8]",
    text: "text-[#0ea5e9]",
    variant: "primary",
  },

  cancelled: {
    bg: "bg-slate-100",
    text: "text-slate-600",
    variant: "muted",
  },
};

const getStatusStyles = (status = "") => {
  const key = normalizeStatus(status);

  if (CUSTOM_STATUS_STYLES[key]) {
    return CUSTOM_STATUS_STYLES[key];
  }

  if (SUCCESS_STATUSES.has(key)) {
    return {
      bg: "bg-[#E7FBF8]",
      text: "text-[#216E4E]",
      variant: "success",
    };
  }

  if (DANGER_STATUSES.has(key)) {
    return {
      bg: key === "burned" ? "bg-[#f7dfdf]" : "bg-[#FFEEED]",
      text: "text-[#C02E24]",
      variant: "muted",
    };
  }

  if (WARNING_STATUSES.has(key)) {
    return {
      bg: "bg-[#FFF6E8]",
      text: "text-[#AD4800]",
      variant: "primary",
    };
  }

  return {
    bg: "bg-[#FFF6E8]",
    text: "text-[#AD4800]",
    variant: "primary",
  };
};

const StatusBadge = memo(
  ({
    status = "",
    label,
    text,
    className = "",
    type = "button",
    as = "button",
    withDot = false,
    disabled = false,
    ...props
  }) => {
    let badgeText = status || "---";

    if (label) badgeText = label;
    if (text) badgeText = text;

    const badgeStyle = getStatusStyles(status);

    const classes = `
      inline-flex items-center justify-center gap-2
      rounded-full px-3 py-1.5
      text-[13px] leading-[1.2]
      font-medium border border-transparent
      ${badgeStyle.bg}
      ${badgeStyle.text}
      ${disabled ? "opacity-50 cursor-not-allowed" : ""}
      ${className}
    `;

    const Dot = withDot ? (
      <span
        className={`w-2 h-2 rounded-full shrink-0 ${badgeStyle.text}`}
      />
    ) : null;

    if (as === "span") {
      return (
        <span className={classes} {...props}>
          {Dot}
          {badgeText?.toUpperCase()}
        </span>
      );
    }

    return (
      <button
        type={type}
        disabled={disabled}
        className={classes}
        {...props}
      >
        {Dot}
        {badgeText?.toUpperCase()}
      </button>
    );
  }
);

StatusBadge.displayName = "StatusBadge";

StatusBadge.propTypes = {
  status: PropTypes.string,
  label: PropTypes.string,
  text: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  className: PropTypes.string,
  type: PropTypes.string,
  disabled: PropTypes.bool,
  title: PropTypes.string,
  as: PropTypes.oneOf(["button", "span"]),
  withDot: PropTypes.bool,
};

export default StatusBadge;